import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BaseModal from './common/BaseModal';
import { commonStyles } from '../styles/colors';
import { useFormattedNumber } from '../hooks/useFormattedNumber';

// House status constants
const HOUSE_STATUSES = {
  UNDERWRITING: 'Underwriting',
  UNQUALIFIED: 'Unqualified',
  UNDER_CONTRACT: 'Under Contract',
  UNDER_MANAGEMENT: 'Under Management',
  DIVESTED: 'Divested'
};

const HOUSE_STATUS_CLASSES = {
  UNDERWRITING: 'bg-primary',
  UNQUALIFIED: 'bg-secondary',
  UNDER_CONTRACT: 'bg-warning',
  UNDER_MANAGEMENT: 'bg-success',
  DIVESTED: 'bg-info'
};

const EditHouseModal = ({ isOpen, onClose, house, propertyId, onUpdate }) => {
  const [status, setStatus] = useState(house?.status || 'UNDERWRITING');
  const [formData, setFormData] = useState({
    street: house?.address?.street || '',
    city: house?.address?.city || '',
    state: house?.address?.state || '',
    zipCode: house?.address?.zipCode || '',
    beds: house?.beds || '',
    baths: house?.baths || '',
    link: house?.link || ''
  });
  
  const squareFootageFormat = useFormattedNumber('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState({});

  // Update form data when house changes
  useEffect(() => {
    if (house) {
      setStatus(house.status || 'UNDERWRITING');
      setFormData({
        street: house.address?.street || '',
        city: house.address?.city || '',
        state: house.address?.state || '',
        zipCode: house.address?.zipCode || '',
        beds: house.beds || '',
        baths: house.baths || '',
        link: house.link || ''
      });
      
      // Update square footage
      if (house.squareFootage) {
        squareFootageFormat.onChange({ target: { value: house.squareFootage.toString() } });
      }
    }
  }, [house]);

  // Function to check if all required fields are filled
  const areAllFieldsFilled = () => {
    return formData.street.trim() !== '' && 
           formData.city.trim() !== '' && 
           formData.state.trim() !== '' && 
           formData.zipCode.trim() !== '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const houseRef = doc(db, 'properties', propertyId);
      const updateData = {
        status,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state.toUpperCase(),
          zipCode: formData.zipCode,
          country: 'USA'
        },
        beds: Number(formData.beds) || 0,
        baths: Number(formData.baths) || 0,
        squareFootage: Number(squareFootageFormat.value) || 0,
        link: formData.link,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(houseRef, updateData);
      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (error) {
      console.error('Error updating house:', error);
      setError('Failed to update house details');
    } finally {
      setIsLoading(false);
    }
  };

  const modalFooter = (
    <div className="d-flex justify-content-end gap-2">
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
        disabled={isLoading}
      >
        Cancel
      </button>
      <button
        type="submit"
        form="editHouseForm"
        className="btn btn-primary"
        disabled={isLoading || !areAllFieldsFilled()}
      >
        {isLoading ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit House"
      footer={modalFooter}
      maxWidth="900px"
      width="70%"
    >
      <form id="editHouseForm" onSubmit={handleSubmit} className="needs-validation" noValidate>
        <div className="mb-3">
          <label className="form-label">Status</label>
          <select
            className="form-select bg-dark text-white border-secondary"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {Object.entries(HOUSE_STATUSES).map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
          </select>
        </div>
        
        <h5 className="mb-3">Address</h5>
        <div className="mb-3">
          <label className="form-label">Street Address <span className="text-danger">*</span></label>
          <input
            type="text"
            className="form-control"
            name="street"
            value={formData.street}
            onChange={handleChange}
            placeholder="Enter street address"
            required
          />
        </div>

        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label">City <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Enter city"
              required
            />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">State <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="Enter state"
              maxLength="2"
              style={{ textTransform: 'uppercase' }}
              required
            />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">ZIP Code <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              placeholder="Enter ZIP code"
              maxLength="5"
              required
            />
          </div>
        </div>

        <h5 className="mt-4 mb-3">Property Details</h5>
        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label">Beds</label>
            <input
              type="number"
              className="form-control"
              name="beds"
              value={formData.beds}
              onChange={handleChange}
              placeholder="Number beds"
              min="0"
              step="1"
            />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Baths</label>
            <input
              type="number"
              className="form-control"
              name="baths"
              value={formData.baths}
              onChange={handleChange}
              placeholder="Number baths"
              min="0"
              step="0.5"
            />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Square Footage</label>
            <input
              type="text"
              className="form-control"
              value={squareFootageFormat.formatted}
              onChange={squareFootageFormat.onChange}
              placeholder="Square feet"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Property Link</label>
          <div className="input-group">
            <span className="input-group-text bg-dark text-white border-secondary">
              <i className="bi bi-link-45deg"></i>
            </span>
            <input
              type="url"
              className="form-control bg-dark text-white border-secondary"
              name="link"
              value={formData.link}
              onChange={handleChange}
              placeholder="https://www.zillow.com/..."
            />
          </div>
          <div className="form-text text-muted">Enter the link to the property</div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
      </form>
    </BaseModal>
  );
};

export default EditHouseModal;
